import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";

/**
 * Copyright IBM Corp. 2020
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */
import axios from 'axios';
import { LocaleAPI } from '../Locale';
import root from 'window-or-global';
/**
 * @constant {string | string} Host for the Translation API call
 * @private
 */

var _host = process && (process.env.REACT_APP_TRANSLATION_HOST || process.env.TRANSLATION_HOST) || 'https://www.ibm.com';
/**
 * Translation API endpoint
 *
 * @type {string}
 * @private
 */


var _endpoint = "".concat(_host, "/common/v18/js/data/jsononly");
/**
 * Session Storage key for translation data
 *
 * @type {string}
 * @private
 */


var _sessionTranslationKey = 'dds-translation';
/**
 * The cache for in-flight or resolved requests for the i18n data, keyed by the initiating locale.
 *
 * @type {object<string, Translation>}
 */

var _requestsTranslation = {};
/**
 * Translation API class with methods for fetching i18n data for ibm.com
 */

var TranslationAPI = /*#__PURE__*/function () {
  function TranslationAPI() {
    _classCallCheck(this, TranslationAPI);
  }

  _createClass(TranslationAPI, null, [{
    key: "clearCache",

    /**
     * Clears the cache.
     */
    value: function clearCache() {
      Object.keys(_requestsTranslation).forEach(function (key) {
        return delete _requestsTranslation[key];
      });

      for (var i = 0; i < sessionStorage.length; ++i) {
        var key = sessionStorage.key(i);

        if (key.indexOf(_sessionTranslationKey) === 0) {
          sessionStorage.removeItem(key);
        }
      }
    }
    /**
     * Returns translation i18n data
     *
     * @param {object} codes object containing lc and cc
     *
     * @returns {Promise<any>} Translation data
     * @example
     * import { TranslationAPI } from '@carbon/ibmdotcom-services';
     *
     * async function getTranslation() {
     *   const response = await TranslationAPI.getTranslation({
     *     lc: 'en',
     *     cc: 'us',
     *   });
     *   return response;
     * }
     */

  }, {
    key: "getTranslation",
    value: function () {
      var _getTranslation = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(codes) {
        var _this = this;

        var lang, country, locale;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                lang = 'en';
                country = 'us';

                if (!(codes && codes.lc && codes.cc)) {
                  _context.next = 7;
                  break;
                }

                lang = codes.lc;
                country = codes.cc;
                _context.next = 12;
                break;

              case 7:
                _context.next = 9;
                return LocaleAPI.getLocale();

              case 9:
                locale = _context.sent;
                lang = locale.lc;
                country = locale.cc;

              case 12:
                return _context.abrupt("return", new Promise(function (resolve, reject) {
                  _this.fetchTranslation(lang, country, resolve, reject);
                }));

              case 13:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      function getTranslation(_x) {
        return _getTranslation.apply(this, arguments);
      }

      return getTranslation;
    }()
    /**
     * Fetches the translation data from sessionStorage or data fetch
     *
     * @param {string} lang Language code
     * @param {string} country Country code
     * @param {Function} resolve resolves the Promise
     * @param {Function} reject rejects the promise
     */

  }, {
    key: "fetchTranslation",
    value: function fetchTranslation(lang, country, resolve, reject) {
      var _this2 = this;

      var sessionTranslation = JSON.parse(sessionStorage.getItem("".concat(_sessionTranslationKey, "-").concat(country, "-").concat(lang)));

      if (sessionTranslation) {
        resolve(sessionTranslation);
      } else {
        var key = "".concat(lang, "-").concat(country);

        if (!_requestsTranslation[key]) {
          var url = "".concat(_endpoint, "/").concat(country).concat(lang, ".json");
          _requestsTranslation[key] = axios.get(url, {
            headers: {
              'Content-Type': 'text/plain',
              origin: _host
            }
          }).then(function (response) {
            return _this2.transformData(response.data);
          }).then(function (data) {
            sessionStorage.setItem("".concat(_sessionTranslationKey, "-").concat(country, "-").concat(lang), JSON.stringify(data));
            return data;
          });
        }

        _requestsTranslation[key].then(resolve, reject);
      }
    }
    /**
     * Transforms translation data
     *
     * @param   {object} data translation data to be transformed
     * @returns {object} Translation data
     */

  }, {
    key: "transformData",
    value: function transformData(data) {
      var _data$profileMenu;

      var signedout = (_data$profileMenu = data.profileMenu) === null || _data$profileMenu === void 0 ? void 0 : _data$profileMenu.signedout;

      if (signedout) {
        var strReplace = 'state=https%3A%2F%2Fwww.ibm.com';
        var loginIdx = signedout.findIndex(function (elem) {
          var _elem$url;

          return ((_elem$url = elem.url) === null || _elem$url === void 0 ? void 0 : _elem$url.indexOf(strReplace)) !== -1;
        });

        if (loginIdx !== -1 && root.location) {
          var location = encodeURIComponent(root.location.href);
          data.profileMenu.signedout[loginIdx].url = signedout[loginIdx].url.replace(strReplace, "state=".concat(location));
        }
      }

      data.footerMenu.push(data.socialFollow);
      return data;
    }
  }]);

  return TranslationAPI;
}();

export default TranslationAPI;